import { createSelector } from 'reselect';
import {
  selectApplicationTosUpdateDates,
  selectHasApplicationTosUpdateDatesLoaded,
} from '../Application/selectors';
import { UserTosStatus } from './types';

export const loggedInUserSelector = (state) => state.LoggedInUser;

export const selectUserTosAcceptanceDates = createSelector(
  loggedInUserSelector,
  (loggedInUser) =>
    loggedInUser.get('tosAcceptanceDates')
      ? loggedInUser.get('tosAcceptanceDates').toJS()
      : null
);

export const selectHasUserTosAcceptanceDatesLoaded = createSelector(
  selectUserTosAcceptanceDates,
  (userTosAcceptanceDates) => !!userTosAcceptanceDates
);

export const makeSelectTosStatus = (tosType) =>
  createSelector(
    selectHasUserTosAcceptanceDatesLoaded,
    selectHasApplicationTosUpdateDatesLoaded,
    selectUserTosAcceptanceDates,
    selectApplicationTosUpdateDates,
    (
      hasUserTosAcceptanceDatesLoaded,
      hasTosDatesLoaded,
      userTosAcceptanceDates,
      applicationTosUpdateDates
    ) => {
      // wait for all the data before deciding if we want to ask user for consent
      // otherwise the dialog/snackbar would be shown incorrectly
      // if data is not ready, return null
      if (
        !hasUserTosAcceptanceDatesLoaded ||
        !hasTosDatesLoaded ||
        !userTosAcceptanceDates ||
        !applicationTosUpdateDates
      )
        return UserTosStatus.LOADING;

      const userTosAcceptanceDate = userTosAcceptanceDates[tosType];
      const applicationTosUpdateDate = applicationTosUpdateDates[tosType];

      if (!userTosAcceptanceDate) return UserTosStatus.NOT_ACCEPTED;

      // this shouldn't happen, applicationTosUpdateDate should always be a
      // timestamp, but just checking if in case new Date() throws error
      if (!applicationTosUpdateDate) return null;

      if (
        new Date(userTosAcceptanceDate) > new Date(applicationTosUpdateDate)
      ) {
        return UserTosStatus.ACCEPTED;
      } else {
        return UserTosStatus.OUTDATED;
      }
    }
  );

// Deprecated
// TODO: remove this once we replace all its usages with selectUserGeneralTosStatus
export const selectNeedUserToAcceptLatestGeneralTos = createSelector(
  makeSelectTosStatus('general'),
  makeSelectTosStatus('ios'),
  (userGeneralTosStatus, userIosTosStatus) => {
    // data is not ready
    if (
      userGeneralTosStatus === UserTosStatus.LOADING ||
      userIosTosStatus === UserTosStatus.LOADING
    )
      return false;

    // iOS terms is the superset of the OL general terms, so if the user has
    // accepted iOS terms already, we don't need to ask them to accept
    // the general terms
    return !(
      userGeneralTosStatus === UserTosStatus.ACCEPTED ||
      userIosTosStatus === UserTosStatus.ACCEPTED
    );
  }
);

// Deprecated
// TODO: remove this once we replace all its usages with selectUserIosTosStatus
export const selectNeedUserToAcceptLatestIosTos = createSelector(
  makeSelectTosStatus('ios'),
  (userIosTosStatus) => {
    // data is not ready
    if (userIosTosStatus === UserTosStatus.LOADING) return false;

    return !(userIosTosStatus === UserTosStatus.ACCEPTED);
  }
);

export const selectUserGeneralTosStatus = createSelector(
  makeSelectTosStatus('general'),
  makeSelectTosStatus('ios'),
  (userGeneralTosStatus, userIosTosStatus) => {
    // data is not ready
    if (
      userGeneralTosStatus === UserTosStatus.LOADING ||
      userIosTosStatus === UserTosStatus.LOADING
    )
      return UserTosStatus.LOADING;

    // iOS terms is the superset of the OL general terms, so if the user has
    // accepted iOS terms already, we don't need to ask them to accept
    // the general terms.
    // so check iOS terms status first
    if (userIosTosStatus === UserTosStatus.ACCEPTED) {
      return UserTosStatus.ACCEPTED;
    }

    return userGeneralTosStatus;
  }
);

export const selectUserIosTosStatus = createSelector(
  makeSelectTosStatus('ios'),
  (userIosTosStatus) => userIosTosStatus
);
